import { useFormContext, useWatch } from 'react-hook-form';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import get from 'lodash/get';
import { DeepDiff } from 'deep-diff';

// app
import styles from './RiskData.styles';
import { FormGrid, Tooltip } from 'components';

import * as utils from 'utils';
import { RISK_DEFINITION_GENERAL } from 'consts';
// mui
import { Box, Grid, makeStyles, Typography, IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import AddCircleIcon from '@material-ui/icons/AddCircle';

const RiskData = ({ handleStep, groups, definitionsFields, riskDataValues, isReQuote, isEndorsement }) => {
  const classes = makeStyles(styles, { name: 'RiskData' })();
  const { control } = useFormContext();
  const definitionsFieldsNoLabel = definitionsFields?.filter((definition) => definition?.type?.toUpperCase() !== 'LABEL') || [];

  const riskValues = useWatch({
    control,
  });

  const endorsementDiff = isEndorsement || isReQuote ? DeepDiff.diff(riskDataValues, riskValues) : [];

  const checkIsFieldEdited = (name) => {
    const previousValue = get(riskDataValues, `${name}`);
    const currentValue = get(riskValues, `${name}`);

    // TODO check array, multi autocomplete value vs BE values
    if (previousValue || currentValue)
      return utils.generic.isValidObject(currentValue)
        ? `${previousValue}` !== `${currentValue?.value}`
        : `${previousValue}` !== `${currentValue}` || false;

    return false;
  };

  const fieldEditedValues = (name, field) => {
    const previousValue = get(riskDataValues, `${name}`);
    const currentValue = get(riskValues, `${name}`);

    return previousValue || currentValue ? (
      <Box minWidth={150}>
        <Grid container spacing={1}>
          <Grid item xs={3} align="left">
            <Typography variant="body2" align="left" component="span">
              {utils.string.t('products.wasLabel')}:
            </Typography>
          </Grid>
          <Grid item xs={9} align="left">
            <Typography variant="body2" align="left" component="span" className={classes.quoteValue}>
              {previousValue || previousValue === false || previousValue === 0 ? renderValue(field, previousValue) : null}
            </Typography>
          </Grid>
          <Grid item xs={3} align="left">
            <Typography variant="body2" align="left" component="span">
              {utils.string.t('products.nowLabel')}:
            </Typography>
          </Grid>
          <Grid item xs={9} align="left">
            <Typography variant="body2" align="left" component="span" className={classes.quoteValue}>
              {currentValue || currentValue === false ? renderValue(field, currentValue) : null}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    ) : null;
  };

  const renderValue = (field, v) => {
    let prefix = '';
    let suffix = '';
    const value = typeof v !== 'undefined' ? v : riskValues[field.name];
    let newValue = value;

    switch (field.type) {
      case 'number':
        newValue = utils.string.t(`format.number`, { value: { number: value } });
        break;
      case 'toggle_group':
        newValue = field.options.find((option) => option.value === value)?.label;
        break;

      case 'toggle':
        newValue = utils.risk.checkBoolean(value);
        break;
      case 'checkbox':
        newValue = utils.risk.checkBoolean(value);
        break;
      case 'datepicker':
        newValue = utils.string.t(`format.date`, { value: { date: value } });
        break;
      case 'time':
      case 'timepicker':
        newValue = typeof value === 'string' ? value : utils.date.formatToTime(value);
        break;
      case 'select': {
        const options = utils.generic.isValidArray(field.options, true) ? field.options : [];
        const option = options.find((o) => String(o.value) === String(value)) || {};

        newValue = option?.label !== 'Select...' ? option.label : '';
        break;
      }
      case 'radio': {
        newValue = value || '';
        break;
      }
      case 'autocompletemui': {
        const options = utils.generic.isValidArray(field.options, true) ? field.options : [];
        const option = options.find((o) => String(o.value) === String(value)) || {};

        newValue = utils.generic.isValidObject(value) ? value?.label : option?.label ? option?.label : value;
        break;
      }
      case 'autocompletemuiAsync': {
        if (utils.generic.isValidArray(value)) {
          newValue = '';
          for (const singleValue of value) {
            newValue = newValue + `${singleValue?.label || singleValue},`;
          }
          newValue = newValue.slice(0, -1);
        } else newValue = value?.label || value;
        break;
      }
      default:
        break;
    }

    // add prefix/suffix for specific fields
    if (field.name === 'distanceToCoast' && value) {
      suffix = ` ${utils.string.t('map.unit.miles')}`;
    }
    // the extra <span /> is used to prevent Material-UI complaining about not receiving a ReactNode
    // this happens if the value is true/false/undefined/null...
    // this workaround prevents errors in case some invalid values fall through the cracks
    return utils.generic.isValidObject(newValue) ? null : (
      <span>
        {prefix}
        {newValue}
        {suffix}
      </span>
    );
  };

  return (
    <FormGrid container spacing={3} data-testid="risk-data">
      {groups.map((group, index) => {
        const fields = utils.risk.getFieldsByGroup(definitionsFieldsNoLabel, group) || [];

        return group === RISK_DEFINITION_GENERAL ? (
          <FormGrid item xs={12} data-testid={`risk-data-${group}`} key={group}>
            <Box className={classes.card}>
              <Box p={2} className={classes.cardTitle}>
                <Typography variant="h3" className={classes.cardTitleHeading}>
                  {startCase(toLower(group))}
                </Typography>
                <IconButton onClick={handleStep(index)} size="small" aria-label="edit">
                  <EditIcon className={classes.editIcon} />
                </IconButton>
              </Box>
              <Box p={2}>
                <FormGrid container spacing={1}>
                  {fields.map((field) => {
                    const fieldName = field?.targetField ? `${field.targetField}.name` : field.name;
                    const value = field?.targetField ? riskValues[field.targetField]?.name : riskValues[field.name];
                    const condition = utils.risk.getCondition(field, definitionsFieldsNoLabel);
                    const refValueCondition = condition && riskValues && get(riskValues, `${condition.name}`);
                    const isConditionValid = condition && utils.risk.isConditionValid(condition, refValueCondition);
                    const isFieldEdited = isReQuote || isEndorsement ? checkIsFieldEdited(fieldName) : false;
                    const isHidden = utils.risk.isHiddenField(field);

                    return !isHidden ? (
                      condition === undefined || (condition && isConditionValid) ? (
                        <FormGrid item xs={12} sm={4} key={`${field.label}-${value}`}>
                          <FormGrid container spacing={1} key={field.label}>
                            <FormGrid item xs={6}>
                              <Typography variant="body2" component="span">
                                {field.label}
                              </Typography>
                            </FormGrid>
                            <FormGrid item xs={6} classes={{ root: classes.flexGrid }}>
                              <Typography
                                variant="body2"
                                component="div"
                                style={{ fontWeight: 'bold' }}
                                classes={{ root: isFieldEdited ? classes.edited : '' }}
                              >
                                {renderValue(field)}
                              </Typography>
                              {isFieldEdited ? (
                                <Tooltip title={fieldEditedValues(fieldName, field)} placement="top" rich style={{ marginLeft: 5 }}>
                                  <InfoOutlinedIcon fontSize="small" />
                                </Tooltip>
                              ) : null}
                            </FormGrid>
                          </FormGrid>
                        </FormGrid>
                      ) : (
                        <FormGrid item xs={12} sm={4} key={`${field.label}-${value}`} />
                      )
                    ) : null;
                  })}
                </FormGrid>
              </Box>
            </Box>
          </FormGrid>
        ) : (
          <FormGrid item xs={12} sm={6} md={4} data-testid={`risk-data-${group}`} key={group}>
            <Box className={classes.card}>
              <Box p={2} className={classes.cardTitle}>
                <Typography variant="h3" className={classes.cardTitleHeading}>
                  {startCase(toLower(group))}
                </Typography>
                <IconButton onClick={handleStep(index)} size="small" aria-label="edit">
                  <EditIcon className={classes.editIcon} />
                </IconButton>
              </Box>
              <Box p={2}>
                {fields
                  .filter((field) => Boolean(field.name) && field.type !== 'label')
                  .map((field, idx) => {
                    const value = riskValues[field.name] || null;
                    const isArrayColumn = utils.risk.isArrayColumn(field) && utils.generic.isValidArray(value);
                    const isArrayTable = utils.risk.isArrayTable(field) && utils.generic.isValidArray(value);
                    const isObject = utils.risk.isObject(field) && utils.generic.isValidObject(value);
                    const valueArray = isArrayColumn || isArrayTable ? value : [value];
                    const arrayKey = isArrayColumn || isArrayTable ? 'arrayItemDef' : isObject ? 'objectDef' : '';

                    const condition = utils.risk.getCondition(field, definitionsFieldsNoLabel);
                    const refValueCondition = condition && riskValues && get(riskValues, `${condition.name}`);
                    const isConditionValid = condition && utils.risk.isConditionValid(condition, refValueCondition);

                    if (isArrayColumn || isArrayTable || isObject) {
                      const { name } = field;
                      const removedItems = endorsementDiff
                        ?.map((diff) => {
                          const [pathName] = diff?.path;
                          if (pathName === name && diff?.item?.kind === 'D') {
                            return diff.item.lhs;
                          }
                          return false;
                        })
                        .filter(Boolean);

                      const addedItems = endorsementDiff
                        ?.map((diff) => {
                          const [pathName] = diff?.path;
                          if (pathName === name && diff?.item?.kind === 'N') {
                            return diff.item.rhs;
                          }
                          return false;
                        })
                        .filter(Boolean);

                      const valueArrayAndRemoved = [...valueArray, ...removedItems];

                      return condition === undefined || (condition && isConditionValid) ? (
                        <FormGrid spacing={2} container key={`${field.label}-${field.name}`}>
                          <FormGrid item xs={12}>
                            {field.label}
                          </FormGrid>

                          {valueArrayAndRemoved.map((v, index) => {
                            const isRemoved = removedItems.includes(v);
                            const isAdded = addedItems.includes(v);

                            return (
                              <FormGrid item xs={12} key={`key-${index}`}>
                                <Box
                                  className={
                                    valueArrayAndRemoved?.length > 1
                                      ? `${classes.card} ${classes.cardArray} ${isRemoved ? `${classes.cardRemoved}` : ``}${
                                          isAdded ? `${classes.cardAdded}` : ``
                                        }`
                                      : ''
                                  }
                                >
                                  {isRemoved ? (
                                    <Box display="flex" alignItems="center" justifyContent="center" style={{ marginBottom: 20 }}>
                                      <RemoveCircleIcon fontSize="small" style={{ color: '#FF0000', marginRight: 10 }} />
                                      <Typography variant="h3" classes={{ h3: classes.removedTitle }}>
                                        Removed
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {isAdded ? (
                                    <Box display="flex" alignItems="center" justifyContent="center" style={{ marginBottom: 20 }}>
                                      <AddCircleIcon fontSize="small" style={{ color: '#2CC6AB', marginRight: 10 }} />
                                      <Typography variant="h3" classes={{ h3: classes.addedTitle }}>
                                        Added
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {field[arrayKey]
                                    .filter((f) => Boolean(f.name))
                                    .map((arrayField) => {
                                      const fieldName = isObject
                                        ? `${field.name}.${arrayField.name}`
                                        : `${field.name}[${index}].${arrayField.name}`;

                                      const isFieldEdited = isReQuote || isEndorsement ? checkIsFieldEdited(fieldName) : false;
                                      const isHidden = utils.risk.isHiddenField(arrayField);
                                      const isTitle = utils.risk.isTitleField(arrayField);

                                      return !isHidden || isTitle ? (
                                        <FormGrid spacing={1} container key={`${arrayField.name}-${arrayField.label}`}>
                                          <FormGrid item xs={6}>
                                            <Typography variant="body2" component="span">
                                              {arrayField.label}
                                            </Typography>
                                          </FormGrid>
                                          <FormGrid item xs={6} classes={{ root: classes.flexGrid }}>
                                            <Typography
                                              variant="body2"
                                              component="span"
                                              style={{ fontWeight: 'bold' }}
                                              classes={{ root: isFieldEdited && !isRemoved && !isAdded ? classes.edited : '' }}
                                            >
                                              {renderValue(arrayField, v[arrayField.name])}
                                            </Typography>
                                            {isFieldEdited && !isRemoved && !isAdded ? (
                                              <Tooltip
                                                title={fieldEditedValues(fieldName, arrayField)}
                                                placement="top"
                                                rich
                                                style={{ marginLeft: 5 }}
                                              >
                                                <InfoOutlinedIcon fontSize="small" />
                                              </Tooltip>
                                            ) : null}
                                          </FormGrid>
                                        </FormGrid>
                                      ) : null;
                                    })}
                                </Box>
                              </FormGrid>
                            );
                          })}
                        </FormGrid>
                      ) : null;
                    }
                    const isFieldEdited = isReQuote || isEndorsement ? checkIsFieldEdited(field.name) : false;
                    const isHidden = utils.risk.isHiddenField(field);

                    return !isHidden && (condition === undefined || (condition && isConditionValid)) ? (
                      <FormGrid container spacing={2} key={`${field.name}-${field.label}`}>
                        <FormGrid item xs={6}>
                          <Typography variant="body2" component="span">
                            {field.label || field.title}
                          </Typography>
                        </FormGrid>
                        <FormGrid item xs={6} classes={{ root: classes.flexGrid }}>
                          <Typography
                            variant="body2"
                            component="span"
                            style={{ fontWeight: 'bold' }}
                            classes={{ root: isFieldEdited ? classes.edited : '' }}
                          >
                            {renderValue(field)}
                          </Typography>
                          {isFieldEdited ? (
                            <Tooltip title={fieldEditedValues(field.name, field)} placement="top" rich style={{ marginLeft: 5 }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </Tooltip>
                          ) : null}
                        </FormGrid>
                      </FormGrid>
                    ) : null;
                  })}
              </Box>
            </Box>
          </FormGrid>
        );
      })}
    </FormGrid>
  );
};

export default RiskData;
